import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import React, { useEffect, useRef, useState } from "react";
import { defaultMessages } from "../../../libs/i18n/default";
import { setAuthenticatedUser } from "../../../shared/actions";
import { extractTrackingData } from "../../trackingUtils";
import FirebaseEmailVerificationSend from "./FirebaseEmailVerificationSend";
import FirebaseEmailVerificationUpdate from "./FirebaseEmailVerificationUpdate";
import { useFeature } from "../FeatureContext";

const FirebaseEmailVerificationCheck = ({ intl, intl: { formatMessage }, subject, onEmailVerified }) => {
  const dispatch = useDispatch();
  const [timer, setTimer] = useState(0);
  const [resendLocked, setResendLocked] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingText, setLoadingText] = useState("Loading");
  const [updating, setUpdating] = useState(false);
  const [readyToVerify, setReadyToVerify] = useState(false);
  const [verificationCode, setVerificationCode] = useState(null);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [resendInFlight, setResendInFlight] = useState(false);
  const [resendAttempted, setResendAttempted] = useState(false);
  const [verifyAttempted, setVerifyAttempted] = useState(false);
  const [requiresLogin, setRequiresLogin] = useState(false);
  const [password, setPassword] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);
  const [authenticatedUserRecord, setAuthenticatedUserRecord] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const resendButtonRef = useRef();

  const clearMessages = () => {
    setError(null);
    setNotice(null);
  };

  const sendGenerateTokenRequest = async ({ showErrors = true, markAttempt = true } = {}) => {
    if (resendLocked) return;

    const trackingData = extractTrackingData(window.location.search);

    if (markAttempt) {
      setResendAttempted(true);
    }

    clearMessages();
    setResendInFlight(true);

    try {
      const resp = await fetch("/authenticated_user_verifications", {
        method: "post",
        credentials: "same-origin",
        headers: {
          "X-Requested-With": "XMLHttpRequest",
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector("meta[name=\"csrf-token\"]").getAttribute("content")
        },
        body: JSON.stringify({
          authenticated_user_verification: {
            entry_point: "signup",
            tracking_data: trackingData
          }
        })
      });

      const data = await resp.json().catch(() => ({}));

      if (!resp.ok || data.success === false) {
        console.info("[FirebaseEmailVerificationCheck] resend failed", { status: resp.status, data });
        if (showErrors) {
          setError(data.message || formatMessage(defaultMessages.clientFirebaseEmailVerificationVerifyFailure));
          setNotice(null);
        } else {
          setError(null);
          setNotice(null);
        }
        setResendLocked(false);
        setTimer(0);
        setResendInFlight(false);
        return;
      }

      console.info("[FirebaseEmailVerificationCheck] resend succeeded", { status: resp.status, data });
      if (data.authenticated_user) {
        dispatch(setAuthenticatedUser(data.authenticated_user));
        if (data.authenticated_user.email_verified) {
          onEmailVerified();
          return;
        }
      }
      setError(null);
      setNotice(formatMessage(defaultMessages.clientFirebaseEmailVerificationResendSuccess));
      setTimer(60);
      setResendLocked(true);
      setResendInFlight(false);
    } catch (err) {
      console.info("[FirebaseEmailVerificationCheck] resend exception", err);
      if (showErrors) {
        setError(formatMessage(defaultMessages.clientFirebaseEmailVerificationVerifyFailure));
        setNotice(null);
      } else {
        setError(null);
        setNotice(null);
      }
      setResendLocked(false);
      setTimer(0);
      setResendInFlight(false);
    }
  };

  const emailVerificationStandalone = useFeature("email_verification_standalone");

  const verifyEmail = async () => {
    setVerifyAttempted(true);
    setLoading(true);
    setReadyToVerify(false);
    setLoadingText(formatMessage(defaultMessages.clientFirebaseEmailVerificationVerifying));

    const verifyUrl = emailVerificationStandalone ? "/authenticated_user_verifications/verify_identity" : "/users/verify_code";

    try {
      const resp = await fetch(verifyUrl, {
        method: "post",
        credentials: "same-origin",
        headers: {
          "X-Requested-With": "XMLHttpRequest",
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector("meta[name=\"csrf-token\"]").getAttribute("content")
        },
        body: JSON.stringify({ code: verificationCode })
      });
      const data = await resp.json();

      if (data.success) {
        console.info("[FirebaseEmailVerificationCheck] verify succeeded", data);
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: "email_verified",
          userType: subject
        });

        if (emailVerificationStandalone) {
          // Check if we have a session
          const meResp = await fetch("/authenticated_users/me", { credentials: "same-origin" });
          if (!meResp.ok) {
            setAuthenticatedUserRecord(data.authenticated_user);
            setRequiresLogin(true);
            setLoading(false);
            return;
          }
        }

        dispatch(setAuthenticatedUser(data.authenticated_user));
        onEmailVerified();
      } else {
        console.info("[FirebaseEmailVerificationCheck] verify failed", data);
        setLoading(false);
        setError(data.message || formatMessage(defaultMessages.clientFirebaseEmailVerificationVerifyFailure));
        setNotice(null);
      }
    } catch (err) {
      console.info("[FirebaseEmailVerificationCheck] verify exception", err);
      setLoading(false);
      setError(formatMessage(defaultMessages.clientFirebaseEmailVerificationVerifyFailure));
      setNotice(null);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    setError(null);

    const kind = authenticatedUserRecord?.kind || (window.location.pathname.includes("host") ? "host" : "guest");
    try {
      const resp = await fetch("/authenticated_user_sessions/login_after_verification", {
        method: "post",
        credentials: "same-origin",
        headers: {
          "X-Requested-With": "XMLHttpRequest",
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector("meta[name=\"csrf-token\"]").getAttribute("content")
        },
        body: JSON.stringify({
          email: authenticatedUserRecord.email,
          password: password,
          kind: kind
        })
      });
      const data = await resp.json();

      if (data.success) {
        dispatch(setAuthenticatedUser(data.authenticated_user));
        onEmailVerified();
      } else {
        setError(data.message || "Login failed. Please check your password.");
        setLoginLoading(false);
      }
    } catch (err) {
      console.error("[FirebaseEmailVerificationCheck] login exception", err);
      setError("An error occurred during login. Please try again.");
      setLoginLoading(false);
    }
  };

  useEffect(() => {
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    const oobCode = urlParams.get("oobCode");

    if (!oobCode) {
      setLoading(false);
      setTimer(60);
      setResendLocked(true);
    } else {
      setLoading(false);
      setVerificationCode(oobCode);
      setReadyToVerify(true);
    }
  }, []);

  useEffect(() => {
    if (loading || !resendLocked) return;

    const intervalId = setInterval(() => {
      setTimer(prevTimer => prevTimer - 1);
    }, 1000);

    return () => {
      clearInterval(intervalId);
    };
  }, [resendLocked, loading]);

  useEffect(() => {
    if (readyToVerify || updating || loading || requiresLogin) return;

    const poll = async () => {
      try {
        const resp = await fetch("/authenticated_users/me", {
          credentials: "same-origin",
          headers: {
            "X-Requested-With": "XMLHttpRequest",
            "Content-Type": "application/json",
            "X-CSRF-Token": document.querySelector("meta[name=\"csrf-token\"]").getAttribute("content")
          }
        });
        if (resp.ok) {
          const data = await resp.json();
          if (data.success && data.authenticated_user) {
            dispatch(setAuthenticatedUser(data.authenticated_user));
            if (data.authenticated_user.email_verified) {
              console.info("[FirebaseEmailVerificationCheck] Poll detected verified status");
              onEmailVerified();
            }
          }
        }
      } catch (err) {
        console.error("[FirebaseEmailVerificationCheck] Poll failed", err);
      }
    };

    const intervalId = setInterval(poll, 5000);
    return () => clearInterval(intervalId);
  }, [readyToVerify, updating, loading, requiresLogin, dispatch, onEmailVerified]);

  useEffect(() => {
    if (loading) return;
    if (timer > 0) return;

    setResendLocked(false);
  }, [timer]);

  if (updating) {
    return (
        <FirebaseEmailVerificationUpdate
            intl={intl}
            setUpdating={setUpdating}
            subject={subject}
        />
    );
  }

  if (requiresLogin) {
    return (
        <div className="text-center verification--container">
            <div className="verification--icon">
                <i className="fa fa-lock" aria-hidden="true" />
            </div>
            <h1 className="verification--header">{formatMessage(defaultMessages.clientFirebaseEmailVerificationLoginHeader)}</h1>
            <p>{formatMessage(defaultMessages.clientFirebaseEmailVerificationLoginInstructions)}</p>
            <form onSubmit={handleLogin} className="verification--login-form">
                <div className="form-group">
                    <div className="input-group">
                        <input
                            type={showPassword ? "text" : "password"}
                            className="form-control"
                            placeholder={formatMessage(defaultMessages.clientFirebaseEmailVerificationPlaceholdersPasswordEnter)}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                            disabled={loginLoading}
                        />
                        <span
                            className="input-group-addon"
                            onClick={() => setShowPassword(!showPassword)}
                            style={{ cursor: "pointer" }}
                        >
                            <i className={`fa fa-eye${showPassword ? "-slash" : ""}`} />
                        </span>
                    </div>
                </div>
                {error && <div className="alert alert-danger">{error}</div>}
                <button type="submit" className="btn btn-primary" disabled={loginLoading}>
                    {loginLoading ? formatMessage(defaultMessages.clientFirebaseEmailVerificationLoginLoading) : formatMessage(defaultMessages.clientFirebaseEmailVerificationLoginButton)}
                </button>
            </form>
        </div>
    );
  }

  if (readyToVerify) {
    return (
        <div className="text-center verification--container">
            <div className="verification--icon">
                <i className="fa fa-envelope" aria-hidden="true" />
            </div>
            <h1 className="verification--header">{formatMessage(defaultMessages.clientFirebaseEmailVerificationHeader)}</h1>
            <p>{formatMessage(defaultMessages.clientFirebaseEmailVerificationVerifyInstructions)}</p>
            <button className="btn btn-primary" onClick={verifyEmail}>
                {formatMessage(defaultMessages.clientFirebaseEmailVerificationVerifyConfirm)}
            </button>
        </div>
    );
  }

  return (
      <FirebaseEmailVerificationSend
          intl={intl}
          timer={timer}
          error={(resendAttempted || verifyAttempted) ? error : null}
          notice={notice}
          resendInFlight={resendInFlight}
          resendButtonRef={resendButtonRef}
          clearMessages={clearMessages}
          setUpdating={setUpdating}
          sendGenerateTokenRequest={sendGenerateTokenRequest}
          loading={loading}
          loadingText={loadingText}
      />
  );
};

FirebaseEmailVerificationCheck.propTypes = {
  page: PropTypes.string,
  intl: PropTypes.object,
  subject: PropTypes.string,
  onEmailVerified: PropTypes.func
};

export default FirebaseEmailVerificationCheck;
